import {
  FilledCircleIcon,
  CircleIcon,
  SettingsIcon,
} from "../../../assets/images/Icons";
import { Box } from "@mui/material";
import SelectMenu from "./select_menu";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./chartContainer.scss";
import CustomSwitch from "../../../components/Switch";
import GraphOptions from "./graph_options";
import BiAxialLineChart from "../../../components/BiAxialLineChart";
import axios from "axios";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { createSearchParams, useSearchParams } from "react-router-dom";
import { channelIds, getChartReportURLByPlatfrom } from "./utils";
import {getUserToken} from "../../../utils/functions/userToken";
import SectionedDropdown from "components/SectionedDropdown/index";
import { getMetricsByPlatform } from "models/metrics/metricsByPlatform";

// Key used in localStorage (persists across tabs, cleared on login)
const STORAGE_KEY = "chartMetricSelections";

function getInitialMetric(searchParams, urlKey, storageKey, fallback = "noMetric") {
  // Priority: URL param > localStorage > fallback
  const fromUrl = searchParams.get(urlKey);
  if (fromUrl) return fromUrl;
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    if (stored[storageKey] && stored[storageKey] !== "noMetric") return stored[storageKey];
  } catch (e) { /* ignore */ }
  return fallback;
}

function saveToStorage(values) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch (e) { /* ignore */ }
}

export default function ChartContainer({hotelList = [], selectedPlatform ,backgroundColor = "white" }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuOptions, setMenuOptions] = useState([]);
  const { platform = [] } = useSelector((state) => state.metaSearch);
  const [loading, setLoading] = useState(false);

  const token = getUserToken() || "";
  const [option1, setOption1] = useState(() => getInitialMetric(searchParams, "m1", "m1"));
  const [option2, setOption2] = useState(() => getInitialMetric(searchParams, "m2", "m2"));
  const [option3, setOption3] = useState(() => getInitialMetric(searchParams, "m3", "m3"));
  const [option4, setOption4] = useState(() => getInitialMetric(searchParams, "m4", "m4"));
  const [compareOption1, setCompareOption1] = useState(() => getInitialMetric(searchParams, "cm1", "cm1"));
  const [compareOption2, setCompareOption2] = useState(() => getInitialMetric(searchParams, "cm2", "cm2"));
  const [compareOption3, setCompareOption3] = useState(() => getInitialMetric(searchParams, "cm3", "cm3"));
  const [compareOption4, setCompareOption4] = useState(() => getInitialMetric(searchParams, "cm4", "cm4"));

  const [apiChartData, setApiChartData] = useState({});
  const [apiComparisonChartData, setApiComparisonChartData] = useState({});
  const [chartData, setChartData] = useState([]);
  const [seriesData, setSeriesData] = useState({});
  const [seriesCompareData, setSeriesCompareData] = useState({});

  const filterRef = useRef(null);
  const [isChartCollapsed] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [filter, setFilter] = useState(() => getInitialMetric(searchParams, "chartFilter", "chartFilter", "daily"));

  // Sync metric selections to both localStorage and URL params
  const syncMetricState = useCallback(
    (updates) => {
      // Save to localStorage (shared across tabs)
      try {
        const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        saveToStorage({ ...current, ...updates });
      } catch (e) { /* ignore */ }

      // Update URL params (replace to avoid polluting history)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value && value !== "noMetric" && !(key === "chartFilter" && value === "daily")) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { account_id = "", } = useSelector(
    (state) => state?.account?.account || {}
  );
  const platformMetrics = getMetricsByPlatform(selectedPlatform);
  const {hotel_id = [], level, group_id = [] } = useSelector(state =>state?.account)
  const {
    currentDateRange = {},
    comparitionDateRange = {},
    compareWith,
  } = useSelector((state) => state?.calender);

  // Keep URL and sessionStorage in sync whenever selections change
  useEffect(() => {
    syncMetricState({
      m1: option1,
      m2: option2,
      m3: option3,
      m4: option4,
      cm1: compareOption1,
      cm2: compareOption2,
      cm3: compareOption3,
      cm4: compareOption4,
      chartFilter: filter,
    });
  }, [
    option1, option2, option3, option4,
    compareOption1, compareOption2, compareOption3, compareOption4,
    filter, syncMetricState,
  ]);

  // Write default dates to URL if not already present
  useEffect(() => {
    if (!currentDateRange?.startDate || !currentDateRange?.endDate) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!next.has("startDate")) {
          next.set("startDate", format(currentDateRange.startDate, "dd-MM-yyyy"));
        }
        if (!next.has("endDate")) {
          next.set("endDate", format(currentDateRange.endDate, "dd-MM-yyyy"));
        }
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDateRange?.startDate, currentDateRange?.endDate]);


  useEffect(() => {
    const params = {
      account_id,
      level,
      start_date: format(
        currentDateRange?.startDate || new Date(),
        "yyyy-MM-dd"
      ),
      end_date: format(currentDateRange?.endDate || new Date(), "yyyy-MM-dd"),
      ...(level === "HOTEL"
        ? { "hotel_id[]": hotel_id }
        : { "group_id[]": group_id }),
      ...(comparitionDateRange?.startDate && comparitionDateRange?.endDate
        ? {
            compare_start_date: format(
              comparitionDateRange?.startDate,
              "yyyy-MM-dd"
            ),
            compare_end_date: format(
              comparitionDateRange?.endDate,
              "yyyy-MM-dd"
            ),
          }
        : {}),
      ...(platform?.length > 1 ? {
            "meta_channel[]": platform?.map((p) => channelIds?.[p?.id]),
          }: {}),
    };

    if (account_id && platform?.length > 0 && level && hotelList?.every(h => h?.account_id.toString() === account_id.toString())) {
      setLoading(true);
        axios
        .post(`${getChartReportURLByPlatfrom(platform)}`, createSearchParams(
                params
            ),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          const data = res?.data;
          const chartDataKeys = ["daily", "monthly", "weekly", "quarterly"];
          const comparisonChartDataKeys = [
            "daily_compare",
            "monthly_compare",
            "weekly_compare",
            "quarterly_compare",
          ];
          const options = data?.metrics?.map((m) => ({
            id: m.id,
            label: m.label,
          }));
          setMenuOptions(options || []);
          setSeriesData(data?.series?.date_series);
          setSeriesCompareData(data?.series?.date_series_compare);

          const validIds = new Set(options?.map((o) => o.id) || []);

          // Only set defaults on first load; on subsequent fetches keep user selection
          // but fall back if the selected metric no longer exists in the new options
          setOption1((prev) => {
            if (prev === "noMetric") return options?.find(o => o.id === "metric_spent")?.id || "noMetric";
            return validIds.has(prev) ? prev : (options?.find(o => o.id === "metric_spent")?.id || "noMetric");
          });
          setOption2((prev) => {
            if (prev === "noMetric") return options?.find(o => o.id === "metric_clicks")?.id || "noMetric";
            return validIds.has(prev) ? prev : (options?.find(o => o.id === "metric_clicks")?.id || "noMetric");
          });
          setOption3((prev) => (prev === "noMetric" || validIds.has(prev) ? prev : "noMetric"));
          setOption4((prev) => (prev === "noMetric" || validIds.has(prev) ? prev : "noMetric"));

          if (compareWith) {
            setCompareOption1((prev) => {
              if (prev === "noMetric") return options?.find(o => o.id === "metric_spent")?.id || "noMetric";
              return validIds.has(prev) ? prev : (options?.find(o => o.id === "metric_spent")?.id || "noMetric");
            });
            setCompareOption2((prev) => {
              if (prev === "noMetric") return options?.find(o => o.id === "metric_clicks")?.id || "noMetric";
              return validIds.has(prev) ? prev : (options?.find(o => o.id === "metric_clicks")?.id || "noMetric");
            });
            setCompareOption3((prev) => (prev === "noMetric" || validIds.has(prev) ? prev : "noMetric"));
            setCompareOption4((prev) => (prev === "noMetric" || validIds.has(prev) ? prev : "noMetric"));
          }
          setApiChartData(
            Object.keys(data || {})
              .filter((key) => chartDataKeys.includes(key))
              .reduce((acc, curr) => {
                acc[curr] = data?.[curr];
                return acc;
              }, {})
          );
          setApiComparisonChartData(
            Object.keys(data || {})
              .filter((key) => comparisonChartDataKeys.includes(key))
              .reduce((acc, curr) => {
                acc[curr.split("_")[0]] = data?.[curr];
                return acc;
              }, {})
          );
          setLoading(false);
        })
        .catch((err) => {
          console.error(err?.message);
          setLoading(false);
        });
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    token,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(platform),
    account_id,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(currentDateRange),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(comparitionDateRange),
    level,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(hotel_id),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(group_id),
    compareWith,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(hotelList),
  ]);
  


  useEffect(() => {
    if (!Object.keys(apiChartData).length) return;

    const seriesDates = seriesData?.[filter];
    if (!seriesDates?.length) return;

    const activeMetrics = [option1, option2, option3, option4].filter((op) => op !== "noMetric");
    const activeCompareMetrics = compareWith
      ? [compareOption1, compareOption2, compareOption3, compareOption4].filter((op) => op !== "noMetric")
      : [];
    const hasCompareData = compareWith && Object.keys(apiComparisonChartData).length > 0;

    const finalChartData = seriesDates.map((date, index) => {
      const lines = activeMetrics.reduce((acc, metric) => {
        const val = Number(apiChartData?.[filter]?.[metric]?.[index]);
        if (!Number.isNaN(val)) acc[metric] = val;
        return acc;
      }, {});

      const lines2 = hasCompareData
        ? activeCompareMetrics.reduce((acc, metric) => {
            const val = Number(apiComparisonChartData?.[filter]?.[metric]?.[index]);
            if (!Number.isNaN(val)) acc[`compare${metric}`] = val;
            return acc;
          }, {})
        : {};

      return { name: date, ...lines, ...lines2 };
    });

      

    setChartData(finalChartData);
  }, [
    apiChartData,
    apiComparisonChartData,
    filter,
    option1,
    option2,
    option3,
    option4,
    compareOption1,
    compareOption2,
    compareOption3,
    compareOption4,
    compareWith,
    seriesData,
  ]);

  const getColors = useMemo(() => {
    const colors = ["#12794F", "#4470C1", "#EA9196", "#46508D"];
    [option1, option2, option3, option4]?.forEach((i, index) => {
      if (i === "noMetric") {
        colors[index] = null;
      }
    });

    return colors.filter((i) => i !== null);
  }, [option1, option2, option3, option4]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: backgroundColor,
          borderRadius: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
              paddingTop: "22px",
          }}
        >
          {isChartCollapsed ? (
            <div className="quency-cf-normal title"> Graph </div>
          ) : (
            ""
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: "flex-end",
              marginLeft: "auto",
              ...(loading ? { opacity: 0.5, pointerEvents: "none" } : {}),
            }}
          >
                  <Box className="menu">
                      <Box className="icon">
                          {option1 === "noMetric" ? <CircleIcon /> : <FilledCircleIcon color="#12794F" />}
                      </Box>
                      <SectionedDropdown
                          menuOptions={[
                              {
                                  id: "noMetric",
                                  label: "No metric" ,
                              },
                              ...menuOptions?.filter(
                                  (item) =>
                                      item?.id !== option2 &&
                                      item?.id !== option3 &&
                                      item?.id !== option4
                              ),
                          ]}
                          platformMetrics={platformMetrics}
                          value={option1}
                          color={getColors[0]}
                          onSelect={(v) => {
                              setOption1(v);
                              if (compareOption1 !== "noMetric") {
                                  setCompareOption1(v);
                              }
                          }}
                          action={compareWith && (
                              <Box sx={{ fontSize: 12 }}>
                                  <CustomSwitch
                                      checked={compareOption1 !== "noMetric" && compareWith}
                                      disabled={option1 === "noMetric"}
                                      onChange={() => {
                                          console.log("Switch clicked");
                                          if (compareOption1 !== "noMetric") {
                                              setCompareOption1("noMetric");
                                          } else {
                                              setCompareOption1(option1);
                                          }
                                      }}

                                  />{" "}
                                  Show Compare{" "}
                              </Box>
                          )}
                      />
                  </Box>

              <Box className="menu">
                  <Box className="icon">
                {option2 === "noMetric" ? (
                  <CircleIcon />
                ) : (
                  <FilledCircleIcon color={"#4470C1"} />
                )}
                  </Box>
                  <SectionedDropdown
                      menuOptions={[
                          {
                              id: "noMetric",
                              label: "No metric",
                          },
                          ...menuOptions?.filter(
                          (item) =>
                              item?.id !== option1 &&
                              item?.id !== option3 &&
                              item?.id !== option4
                      ),
                      ]}
                      color={getColors[1]}
                      value={option2}
                      onSelect={(v) => {
                          setOption2(v);
                          if (compareOption2 !== "noMetric") {
                            setCompareOption2(v);
                          }
                      }}
                      platformMetrics={platformMetrics}
                      action={compareWith && (
                          <Box sx={{ fontSize: 12 }}>
                              <CustomSwitch
                                  checked={compareOption2 !== "noMetric" && compareWith}
                                  disabled={option2 === "noMetric"}
                                  onChange={() => {
                                    if (compareOption2 !== "noMetric") {
                                      setCompareOption2("noMetric");
                                    } else {
                                      setCompareOption2(option2);
                                    }
                                  }}
                                />{" "}
                        Show Compare{" "}
                          </Box>
                      )}
                  />
              </Box>

              <Box className="menu">
                  <Box className="icon">
                {option3 === "noMetric" ? (
                  <CircleIcon />
                ) : (
                  <FilledCircleIcon
                    color={option3 === "noMetric" ? "transparent" : "#EA9196"}
                  />
                )}
                  </Box>
                  <SectionedDropdown
                      menuOptions={[
                          {
                              id: "noMetric",
                              label: "No metric" ,
                          },
                          ...menuOptions?.filter(
                          (item) =>
                              item?.id !== option1 &&
                              item?.id !== option2 &&
                              item?.id !== option4
                            ),
                      ]}
                      color={getColors[2]}
                      value={option3}
                      onSelect={(v) => {
                          setOption3(v);
                          if (compareOption3 !== "noMetric") {
                            setCompareOption3(v);
                          }
                      }}
                      platformMetrics={platformMetrics}
                      action={compareWith && (
                          <Box sx={{ fontSize: 12 }}>
                              <CustomSwitch
                                  checked={compareOption3 !== "noMetric" && compareWith}
                                  disabled={option3 === "noMetric"}
                                  onChange={() => {
                                    if (compareOption3 !== "noMetric") {
                                      setCompareOption3("noMetric");
                                    } else {
                                      setCompareOption3(option3);
                                    }
                                  }}
                        />{" "}
                        Show Compare{" "}
                          </Box>
                      )}
                  />
              </Box>

              <Box className="menu">
                  <Box className="icon">
                      {option4 === "noMetric" ? <CircleIcon /> : <FilledCircleIcon color="#46508D" />}
                  </Box>
                  <SectionedDropdown
                      menuOptions={[
                          {
                              id: "noMetric",
                              label: "No metric",
                          }
                          , ...menuOptions.filter(
                          (item) =>
                          item?.id !== option2 &&
                          item?.id !== option3 &&
                          item?.id !== option1
                      ),
                  ]}
                      color={getColors[3]}
                      platformMetrics={platformMetrics}
                      value={option4}
                      onSelect={(v) => {
                          setOption4(v);
                          if (compareOption4 !== "noMetric") {
                            setCompareOption4(v);
                          }
                      }}
                      action={compareWith && (
                          <Box sx={{ fontSize: 12 }}>
                              <CustomSwitch
                                  checked={compareOption4 !== "noMetric" && compareWith}
                                  disabled={option4 === "noMetric"}
                                  onChange={() => setCompareOption4(compareOption4 !== "noMetric" ? "noMetric" : option4)}
                              />
                              Show Compare
                          </Box>
                      )}
                  />
              </Box>

            <Box className="filters">
              <Box
                onClick={() => {
                  setIsFilterMenuOpen(!isFilterMenuOpen);
                }}
                ref={filterRef}
              >
                <SettingsIcon color="#12794F" />
                <GraphOptions
                  open={isFilterMenuOpen}
                  anchorEl={filterRef}
                  onClose={() => {
                    setIsFilterMenuOpen(false);
                  }}
                  defaultActive={filter}
                  onChange={(value) => {
                    setFilter(value);
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: "197px",
            display: isChartCollapsed ? "none" : "block",
          }}
        >
          <BiAxialLineChart
            menuOptions={menuOptions}
            data={chartData}
            colors={getColors}
            compareSeries={seriesCompareData?.[filter]}
            loading={loading}
          />
        </Box>
      </Box>
    </>
  );
}
